import { useEffect, useRef, type JSX } from 'react';
import { useClockOffset } from '../clock.js';
import { prefersReducedMotion } from '../../preferences/reducedMotion.js';
import './Hourglass.css';

interface HourglassProps {
  /** Unix ms when the phase ends. `null` freezes sand at the full-top position. */
  phaseEndsAt: number | null;
  /** Total phase duration in ms (i.e. `phaseEndsAt − phaseStartedAt`). */
  durationMs: number;
}

/**
 * Woodcut SVG hourglass. The frame is static; the two sand polygons recompute
 * each `requestAnimationFrame` from a fractional progress value (0 → 1) so the
 * upper bulb drains and the lower bulb fills smoothly. Polygons are mutated
 * directly via refs to avoid re-rendering the SVG 60 times a second.
 *
 * Geometry (viewBox 24×28):
 *   - Top edge at y=2, bottom edge at y=26.
 *   - Upper bulb: y=2 → y=13 (11 units tall).
 *   - Neck: y=13 → y=15.
 *   - Lower bulb: y=15 → y=26.
 * Frame is two separate triangle paths so each apex's miter join extends
 * outward into the empty neck channel (not into the sand area).
 *
 * Reduced motion freezes both polygons at the initial state — the count
 * digit beside the hourglass (rendered by Timer or any other parent) still
 * ticks once per second, which is enough to convey countdown.
 */
export function Hourglass({ phaseEndsAt, durationMs }: HourglassProps): JSX.Element {
  const topRef = useRef<SVGPolygonElement>(null);
  const bottomRef = useRef<SVGPolygonElement>(null);
  const offset = useClockOffset();

  useEffect(() => {
    if (phaseEndsAt === null) return;
    const reducedMotion = prefersReducedMotion();
    if (reducedMotion) return;

    const phaseStart = phaseEndsAt - durationMs;
    let raf = 0;
    /* Progress at the last frame that actually wrote to the DOM. Initialized
       to -1 (a sentinel below any valid progress) so the first tick always
       renders. Reset implicitly on every phase change because this whole
       effect closure is recreated when [phaseEndsAt, durationMs] changes. */
    let lastRenderedP = -1;
    /* Epsilon guard: skip setAttribute when progress hasn't advanced enough
       to move a vertex by even a fraction of a sub-pixel. At a 28px rendered
       hourglass on a 30s phase, each 60Hz frame advances p by ~0.00056,
       moving vertices by ~0.005 SVG units (~0.006 CSS px) — well below
       perceptual threshold. Updating only every 1/200 of progress drops
       ~89% of per-frame DOM writes with zero perceptual change. The
       `p === 1` clause guarantees the terminal frame always lands. */
    const EPSILON = 1 / 200;

    const tick = () => {
      const now = Date.now() + offset;
      const p = Math.min(1, Math.max(0, (now - phaseStart) / durationMs));
      if (p - lastRenderedP >= EPSILON || p === 1) {
        lastRenderedP = p;
        /* Top: starts as a full triangle filling the upper bulb (3,2 → 21,2 → 12,13);
           shrinks as `p` grows, vertices sliding down toward the neck at (12,13). */
        if (topRef.current) {
          topRef.current.setAttribute(
            'points',
            `${3 + 9 * p},${2 + 11 * p} ${21 - 9 * p},${2 + 11 * p} 12,13`,
          );
        }
        /* Bottom: starts as a degenerate triangle pinned at the floor (no sand);
           apex rises from y=26 toward the neck at y=15 as `p` grows. */
        if (bottomRef.current) {
          bottomRef.current.setAttribute(
            'points',
            `3,26 21,26 12,${26 - 11 * p}`,
          );
        }
      }
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phaseEndsAt, durationMs, offset]);

  return (
    <svg
      className="susdle-hourglass"
      viewBox="0 0 24 28"
      aria-hidden
      focusable={false}
    >
      {/* Outer frame — split into upper + lower triangles. A single path
          tracing the full hourglass outline puts a miter join at the neck
          corner (12,13) whose tip extends INTO the upper bulb's interior,
          creating a visible notch in the sand triangle's apex as it
          drains. Two separate closed triangles instead: each triangle's
          apex miter now extends outward into the empty neck channel,
          where there's no sand to interrupt. */}
      <path
        d="M 3 2 L 21 2 L 12 13 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="miter"
      />
      <path
        d="M 3 26 L 21 26 L 12 15 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="miter"
      />
      <polygon
        ref={topRef}
        points="3,2 21,2 12,13"
        fill="currentColor"
      />
      <polygon
        ref={bottomRef}
        points="3,26 21,26 12,26"
        fill="currentColor"
      />
      {/* Stream line at the neck. */}
      <line
        x1="12" y1="13" x2="12" y2="15"
        stroke="currentColor" strokeWidth="0.9" strokeLinecap="round"
      />
      {/* Top and bottom capping rules — printed-engraving convention. */}
      <line
        x1="1.5" y1="2" x2="22.5" y2="2"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"
      />
      <line
        x1="1.5" y1="26" x2="22.5" y2="26"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"
      />
    </svg>
  );
}
